<template>
  <div v-if="isOpen" class="modal-overlay">
    <div class="share-modal">
      <h3 class="text-2xl font-bold mb-4 text-gray-900 flex items-center">
        <font-awesome-icon :icon="['fas', 'share-alt']" class="mr-2 text-blue-500" />
        {{ $translate("event.share.title") }}
      </h3>
      <p class="text-gray-600 mb-5">
        {{ $translate("event.share.description") }}
      </p>

      <!-- Email Invitation Section -->
      <form @submit.prevent="openInviteDialog">
        <div class="mb-6">
          <label for="email" class="block text-sm font-medium text-gray-700">
            {{ $translate("event.share.send_email_invite") }}
          </label>
          <div class="flex mt-2">
            <input
              type="email"
              v-model="email"
              id="email"
              :placeholder="$translate('event.share.email_placeholder')"
              class="block w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:outline-none transition-all duration-300"
              :disabled="!isLoggedIn"
              required
            />
            <button
              type="submit"
              class="ml-2 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 w-1/2 flex items-center justify-center disabled:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-75 transition-all duration-300 hover:shadow-md"
              :disabled="!isLoggedIn"
            >
              <span class="mr-2">{{
                $translate("event.share.send_email")
              }}</span>
              <font-awesome-icon :icon="['fas', 'paper-plane']" />
            </button>
          </div>
          <p
            v-if="!isLoggedIn"
            class="text-red-500 mt-2"
            v-html="
              $translate('event.share.login_required', {
                loginUrl: loginUrl,
                signupUrl: signupUrl,
              })
            "
          ></p>
        </div>

        <!-- Share via URL -->
        <div class="mb-6">
          <label for="url" class="block text-sm font-medium text-gray-700">
            {{ $translate("event.share.copy_link") }}
          </label>
          <div class="flex mt-2">
            <input
              type="text"
              :value="fullEventUrl"
              id="url"
              class="block w-full p-3 border rounded-md"
              readonly
            />
            <button
              type="button"
              @click="copyUrl"
              class="ml-2 px-2 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 w-1/2 flex items-center justify-center transition-all duration-300 hover:shadow-md"
            >
              <span class="mr-2">{{
                $translate("event.share.copy_link")
              }}</span>
              <font-awesome-icon :icon="['fas', 'clipboard']" />
            </button>
          </div>
        </div>

        <!-- Share via Social Media -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700">
            {{ $translate("event.share.share_social") }}
          </label>
          <div class="flex space-x-3 mt-4 justify-center">
            <SFacebook
              :share-options="{
                url: fullEventUrl,
                quote: eventExcerpt,
                hashtag: eventHashtag,
              }"
              @click="sendFeedback('facebook')"
              class="social-button facebook-button"
            >
              <font-awesome-icon :icon="['fab', 'facebook']" class="text-xl" />
            </SFacebook>
            <STwitter
              :share-options="{
                url: fullEventUrl,
                text: eventExcerpt,
                hashtags: [eventHashtag],
              }"
              @click="sendFeedback('twitter')"
              class="social-button twitter-button"
            >
              <font-awesome-icon :icon="['fab', 'twitter']" class="text-xl" />
            </STwitter>
            <SLinkedIn
              :share-options="{
                url: fullEventUrl,
                title: eventTitle,
                summary: eventExcerpt,
              }"
              @click="sendFeedback('linkedin')"
              class="social-button linkedin-button"
            >
              <font-awesome-icon :icon="['fab', 'linkedin']" class="text-xl" />
            </SLinkedIn>
            <SWhatsApp
              :share-options="{ url: fullEventUrl, text: eventExcerpt }"
              @click="sendFeedback('whatsapp')"
              class="social-button whatsapp-button"
            >
              <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-xl" />
            </SWhatsApp>
          </div>
        </div>

        <!-- Close Button -->
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-all duration-300 hover:shadow-md"
          >
            {{ $translate("event.share.close") }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Invite Dialog Modal -->
  <div v-if="inviteDialogOpen" class="modal-overlay">
    <div class="share-modal">
      <h3 class="text-2xl font-bold mb-4 text-gray-900 flex items-center">
        <font-awesome-icon :icon="['fas', 'envelope']" class="mr-2 text-blue-500" />
        {{ $translate("event.share.invite_dialog_title") }}
      </h3>
      <form @submit.prevent="confirmSendInvites">
        <!-- Email addresses input -->
        <div class="mb-4">
          <label
            for="inviteEmails"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $translate("event.share.email_addresses") }}
          </label>
          <input
            type="text"
            v-model="inviteEmails"
            id="inviteEmails"
            placeholder="email1@example.com, email2@example.com"
            class="block w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:outline-none"
            :class="{
              'border-red-500':
                inviteEmails &&
                (!areEmailsValid || !maxEmailsValid || hasDuplicates),
            }"
            required
          />
          <p
            v-if="inviteEmails && !areEmailsValid"
            class="text-xs text-red-500 mt-1"
          >
            {{
              $translate("event.share.invalid_email_format") ||
              "One or more email addresses are invalid."
            }}
          </p>
          <p v-if="!maxEmailsValid" class="text-xs text-red-500 mt-1">
            {{
              $translate("event.share.max_emails_error") ||
              "Maximum 20 email addresses allowed."
            }}
          </p>
          <p v-if="hasDuplicates" class="text-xs text-red-500 mt-1">
            {{
              $translate("event.share.duplicate_emails_error") ||
              "Duplicate email addresses are not allowed."
            }}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            {{ $translate("event.share.email_help_text") }}
          </p>
        </div>

        <!-- Custom message textarea with character counter and error -->
        <div class="mb-4">
          <label
            for="customMessage"
            class="block text-sm font-medium text-gray-700"
          >
            {{ $translate("event.share.optional_message") }}
          </label>
          <textarea
            v-model="customMessage"
            id="customMessage"
            :placeholder="$translate('event.share.message_placeholder')"
            class="block w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:outline-none"
            @input="updateMessageCount"
            maxlength="300"
            :class="{
              'border-red-500': customMessage && customMessage.length >= 300,
            }"
          ></textarea>
          <div class="text-right text-xs text-gray-500 mt-1">
            {{ messageCount }}/300
          </div>
          <p
            v-if="customMessage && customMessage.length >= 300"
            class="text-xs text-red-500 -mt-3"
          >
            {{
              $translate("event.share.message_length_error") ||
              "Custom message must be 300 characters or less."
            }}
          </p>
          <p
            class="text-xs text-gray-500 mt-1"
            v-html="$translate('event.share.message_help_text')"
          ></p>
        </div>

        <!-- Dialog action buttons -->
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            @click="closeInviteDialog"
            class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-all duration-300 hover:shadow-md"
          >
            {{ $translate("event.share.cancel") }}
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-75 flex items-center transition-all duration-300 hover:shadow-md"
            :disabled="isInviteButtonDisabled"
          >
            <font-awesome-icon :icon="['fas', 'paper-plane']" class="mr-2" />
            {{ buttonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
  
<script>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { SFacebook, STwitter, SLinkedIn, SWhatsApp } from "vue-socials";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { notify } from "notiwind";
import { cookie, getUserData } from "@/utils/cookie";
import { translate } from "@/utils/translate";
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { 
  faShareAlt, 
  faPaperPlane, 
  faClipboard, 
  faEnvelope 
} from "@fortawesome/free-solid-svg-icons";
import { generateHashtag, getSystemPath } from "@/utils/helper";
import { sendUserAction } from "@/utils/socket";

library.add(
  faFacebook, 
  faTwitter, 
  faLinkedin, 
  faWhatsapp, 
  faShareAlt, 
  faPaperPlane, 
  faClipboard, 
  faEnvelope
);

export default {
  name: "ShareModal",
  components: {
    SFacebook,
    STwitter,
    SLinkedIn,
    SWhatsApp,
    FontAwesomeIcon,
  },
  emits: ['close'],
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    eventUrl: {
      type: String,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    vAFh: {
      type: [String, Boolean],
      required: true,
    },
    eventId: {
      type: [String, Boolean],
      required: true,
    },
    isSignedUp: {
      type: Boolean,
      default: false
    },
  },
  setup(props, { emit }) {
    // Original invitation email (from the main dialog)
    const email = ref("");
    const sessionId = cookie();

    const fullEventUrl = computed(() =>
      props.vAFh ? `${props.eventUrl}?vafh=${props.vAFh}` : props.eventUrl
    );
    const eventHashtag = computed(() => generateHashtag(props.event.title));
    const eventTitle = computed(() => props.event.title);
    const eventExcerpt = computed(() =>
      props.event.excerpt ? props.event.excerpt : props.event.title
    );

    // User and route information
    const userData = getUserData();
    const isLoggedIn = computed(() => !!(userData && userData.id));
    const route = useRoute();
    const loginUrl = computed(
      () =>
        `/${getSystemPath("login")}?redirect=${encodeURIComponent(
          route.fullPath
        )}`
    );
    const signupUrl = computed(
      () =>
        `/${getSystemPath("register")}?redirect=${encodeURIComponent(
          route.fullPath
        )}`
    );

    // Invite dialog state and fields
    const inviteDialogOpen = ref(false);
    const inviteEmails = ref("");
    const customMessage = ref("");
    const messageCount = ref(0);

    const updateMessageCount = () => {
      messageCount.value = customMessage.value.length;
    };

    // Instead of sending the invite immediately, open the invite dialog
    const openInviteDialog = () => {
      inviteEmails.value = email.value; // prefill with the email entered above
      inviteDialogOpen.value = true;
    };

    const closeInviteDialog = () => {
      inviteDialogOpen.value = false;
    };

    // Simple email validation regex
    const validateEmail = (email) => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    };

    // Computed property to split emails, trim spaces and filter out empties
    const emailsArray = computed(() =>
      inviteEmails.value
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e !== "")
    );

    // Check if every email is valid
    const areEmailsValid = computed(() =>
      emailsArray.value.every((email) => validateEmail(email))
    );

    // Ensure there is at least one email
    const hasAtLeastOneEmail = computed(() => emailsArray.value.length > 0);

    // Check for max emails (20 or less)
    const maxEmailsValid = computed(() => emailsArray.value.length <= 20);

    // Check for duplicate emails
    const hasDuplicates = computed(
      () => new Set(emailsArray.value).size < emailsArray.value.length
    );

    const isInviteButtonDisabled = computed(
      () =>
        !hasAtLeastOneEmail.value ||
        !areEmailsValid.value ||
        !maxEmailsValid.value ||
        hasDuplicates.value
    );

    // Compute the button text based on the number of email addresses.
    const buttonText = computed(() => {
      const count = emailsArray.value.length;

      if (count === 1 || count === 0) {
        return translate("event.share.send_invite");
      } else if (count >= 5) {
        return translate("event.share.send_invites_plural", { count });
      } else {
        return translate("event.share.send_invites", { count });
      }
    });

    // Validate and send invites from the invite dialog
    const confirmSendInvites = async () => {
      // Split emails by comma, trim spaces, and filter out empties
      const emailsArray = inviteEmails.value
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e !== "");

      if (emailsArray.length === 0) {
        notify(
          {
            title: translate("Error"),
            text:
              translate("event.share.no_email_error") ||
              "Please provide at least one valid email address.",
            type: "error",
            group: "error",
          },
          10000
        );
        return;
      }

      // Validate each email
      for (const emailItem of emailsArray) {
        if (!validateEmail(emailItem)) {
          notify(
            {
              title: translate("Error"),
              text:
                translate("event.share.invalid_email_error") ||
                `Invalid email address: ${emailItem}`,
              type: "error",
              group: "error",
            },
            10000
          );
          return;
        }
      }

      // Validate maximum emails
      if (!maxEmailsValid.value) {
        notify(
          {
            title: translate("Error"),
            text:
              translate("event.share.max_emails_error") ||
              "Maximum 20 email addresses allowed.",
            type: "error",
            group: "error",
          },
          10000
        );
        return;
      }

      // Validate duplicate emails
      if (hasDuplicates.value) {
        notify(
          {
            title: translate("Error"),
            text:
              translate("event.share.duplicate_emails_error") ||
              "Duplicate email addresses are not allowed.",
            type: "error",
            group: "error",
          },
          10000
        );
        return;
      }

      // Validate custom message length
      if (customMessage.value.length > 300) {
        notify(
          {
            title: translate("Error"),
            text:
              translate("event.share.message_length_error") ||
              "Custom message must be 300 characters or less.",
            type: "error",
            group: "error",
          },
          10000
        );
        return;
      }

      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/attendee/invite`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              emails: emailsArray, // sending as an array of email addresses
              event_id: props.eventId,
              custom_message: customMessage.value,
              session_id: sessionId,
              brand_id: window.vueAppConfig.info.id,
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          sendUserAction("invite", "share");
          notify(
            {
              title: translate("Success"),
              text: data.message,
              type: "info",
              group: "info",
            },
            15000
          );
          // Clear the fields after a successful invite
          email.value = "";
          customMessage.value = "";
          inviteEmails.value = "";
          messageCount.value = 0;
          closeInviteDialog();
        } else {
          notify(
            {
              title: translate("Error"),
              text: data.message,
              type: "error",
              group: "error",
            },
            10000
          );
        }
      } catch (error) {
        console.error("Error sending invitation:", error);
        notify(
          {
            title: translate("Error"),
            text: translate("An error occurred while sending the invitation."),
            type: "error",
            group: "error",
          },
          10000
        );
      }
    };

    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(fullEventUrl.value);
        sendUserAction("url", "share");
        notify(
          {
            title: translate("Success"),
            text: translate("event.share.copy_link_success"),
            type: "info",
            group: "info",
          },
          15000
        );
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    };

    const sendFeedback = async (eventName) => {
      sendUserAction(eventName, "share");
    };

    const closeModal = () => {
      emit("close");
    };

    return {
      email,
      fullEventUrl,
      copyUrl,
      openInviteDialog,
      closeModal,
      sendFeedback,
      eventHashtag,
      eventTitle,
      eventExcerpt,
      isLoggedIn,
      loginUrl,
      signupUrl,
      // Invite dialog fields and methods
      inviteDialogOpen,
      inviteEmails,
      customMessage,
      messageCount,
      updateMessageCount,
      closeInviteDialog,
      confirmSendInvites,
      areEmailsValid,
      isInviteButtonDisabled,
      maxEmailsValid,
      hasDuplicates,
      emailsArray,
      buttonText,
    };
  },
};
</script>
  
<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
}

.share-modal {
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.social-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  color: white;
}

.social-button:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.facebook-button {
  background-color: #3b5998;
}

.twitter-button {
  background-color: #1da1f2;
}

.linkedin-button {
  background-color: #0077b5;
}

.whatsapp-button {
  background-color: #25d366;
}

@media (max-width: 640px) {
  .share-modal {
    padding: 1.5rem;
    max-width: 100%;
  }
}
</style>
